/**
 * Price Service - Fetch real-time cryptocurrency prices
 */

// CoinGecko API base URL
const COINGECKO_API_URL = 'https://api.coingecko.com/api/v3';

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

class PriceService {
  constructor() {
    this.priceCache = {
      solana: {
        usd: null,
        timestamp: 0
      }
    };
    this.fetchPromise = null;
    
    // Set up a single interval for the entire application
    this.setupPriceUpdateInterval();
  }
  
  // Set up a single interval to update prices every 5 minutes
  setupPriceUpdateInterval() {
    // Initial fetch
    this.getSolanaPrice();
    
    // Then set interval for every 5 minutes
    setInterval(() => {
      this.getSolanaPrice();
    }, CACHE_DURATION);
  }

  /**
   * Get the current price of Solana in USD
   * @returns {Promise<number>} - SOL price in USD
   */
  async getSolanaPrice() {
    try {
      // Check if cache is valid
      const now = Date.now();
      if (this.priceCache.solana.usd && now - this.priceCache.solana.timestamp < CACHE_DURATION) {
        // Return cached price without logging to reduce console noise
        return this.priceCache.solana.usd;
      }
      
      // If there's already a fetch in progress, return that promise
      if (this.fetchPromise) {
        return this.fetchPromise;
      }

      // Create a new fetch promise
      this.fetchPromise = (async () => {
        try {
          // Fetch price from CoinGecko
          console.log('Fetching fresh SOL price from CoinGecko...');
          const response = await fetch(`${COINGECKO_API_URL}/simple/price?ids=solana&vs_currencies=usd`);
          const data = await response.json();

          if (data && data.solana && data.solana.usd) {
            // Update cache
            const price = data.solana.usd;
            console.log('Received SOL price from CoinGecko:', price);
            this.priceCache.solana = {
              usd: price,
              timestamp: now
            };
            return price;
          } else {
            throw new Error('Invalid response from CoinGecko API');
          }
        } catch (error) {
          console.error('Failed to fetch Solana price:', error);
          // Return cached price if available, otherwise fallback to a default value
          return this.priceCache.solana.usd || 140.2; // Updated fallback price to current market value
        } finally {
          // Clear the fetch promise so we can fetch again next time
          this.fetchPromise = null;
        }
      })();
      
      return this.fetchPromise;
    } catch (error) {
      console.error('Unexpected error in getSolanaPrice:', error);
      return this.priceCache.solana.usd || 140.2;
    }
  }

  /**
   * Convert SOL amount to USD
   * @param {number} solAmount - Amount in SOL
   * @returns {Promise<number>} - Equivalent amount in USD
   */
  async solToUsd(solAmount) {
    const solPrice = await this.getSolanaPrice();
    return solAmount * solPrice;
  }

  /**
   * Convert USD amount to SOL
   * @param {number} usdAmount - Amount in USD
   * @returns {Promise<number>} - Equivalent amount in SOL
   */
  async usdToSol(usdAmount) {
    const solPrice = await this.getSolanaPrice();
    return usdAmount / solPrice;
  }

  /**
   * Format USD amount for display
   * @param {number} amount - Amount in USD
   * @returns {string} - Formatted USD amount
   */
  formatUsd(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }

  /**
   * Format SOL amount for display
   * @param {number} amount - Amount in SOL
   * @returns {string} - Formatted SOL amount
   */
  formatSol(amount) {
    return `${amount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 6
    })} SOL`;
  }
}

// Create a singleton instance
const priceService = new PriceService();
export default priceService;
